import { RELEASE_CONFIG } from '@signbento/domain'
import type { TemplateSummary } from './templates.js'

/**
 * Recently Deleted — S2.2 (TPL-04, AT-026, PRD §5.2/§14.2).
 *
 * Soft-deleted templates keep their identity for exactly the register's
 * `template_soft_delete_days` window. The boundary is exact and half-open:
 *
 *   deleted_at + 30 days  →  restore is refused, purge is due
 *
 * so a restore one millisecond before the boundary succeeds and a purge at the
 * boundary (or later) removes the template. Purging is idempotent, and neither
 * delete nor restore nor purge touches a draft, a document, a completion or any
 * other artifact derived from the template.
 */

export const TEMPLATE_SOFT_DELETE_DAYS = RELEASE_CONFIG.template_soft_delete_days.value

export const TEMPLATE_SOFT_DELETE_MS = TEMPLATE_SOFT_DELETE_DAYS * 24 * 60 * 60 * 1000

/** The instant at which a soft-deleted template becomes purgeable. */
export function templateRetentionBoundary(deletedAt: string): string {
  return new Date(new Date(deletedAt).getTime() + TEMPLATE_SOFT_DELETE_MS).toISOString()
}

/** Strictly before the boundary: the template can still come back unchanged. */
export function isTemplateRestorable(deletedAt: string, now: Date): boolean {
  return now.getTime() < new Date(templateRetentionBoundary(deletedAt)).getTime()
}

/** At or after the boundary: purge removes it, exactly once. */
export function isTemplatePurgeDue(deletedAt: string, now: Date): boolean {
  return !isTemplateRestorable(deletedAt, now)
}

export interface RecentlyDeletedTemplate {
  readonly summary: TemplateSummary
  /** When the template is purged for good. */
  readonly purge_at: string
  /** False once the boundary has passed — the record is removed by maintenance. */
  readonly restorable: boolean
  readonly deleted_at: string
}

/**
 * The Recently Deleted view: soft-deleted templates, most recently deleted first,
 * each with its retention boundary. Deleted templates are never part of the
 * catalog (Insights and the library both hide them), so this is the only surface
 * that lists them.
 */
export function recentlyDeletedTemplates(
  summaries: readonly TemplateSummary[],
  now: Date,
): readonly RecentlyDeletedTemplate[] {
  return summaries
    .flatMap((summary) =>
      summary.deleted_at === undefined
        ? []
        : [
            {
              summary,
              purge_at: templateRetentionBoundary(summary.deleted_at),
              restorable: isTemplateRestorable(summary.deleted_at, now),
              deleted_at: summary.deleted_at,
            },
          ],
    )
    .sort((left, right) =>
      left.deleted_at === right.deleted_at
        ? left.summary.id < right.summary.id
          ? -1
          : 1
        : left.deleted_at < right.deleted_at
          ? 1
          : -1,
    )
}
